// Device signal for fraud checks: a random id kept in localStorage plus a hash of stable browser
// traits. Sent as `x-device` on API calls. No canvas/audio fingerprinting, nothing personal.
let cached: Promise<string> | null = null;

function randomHex(bytes: number): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(bytes)), (b) =>
    b.toString(16).padStart(2, '0'),
  ).join('');
}

async function compute(): Promise<string> {
  let id: string | null = null;
  try {
    id = localStorage.getItem('gp_did');
    if (!id || !/^[0-9a-f]{32}$/.test(id)) {
      id = randomHex(16);
      localStorage.setItem('gp_did', id);
    }
  } catch {
    id = randomHex(16); // storage blocked: still send a per-tab id
  }
  const n = navigator as Navigator & { deviceMemory?: number };
  const traits = [
    navigator.userAgent,
    navigator.language,
    (navigator.languages ?? []).join(','),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    String(window.devicePixelRatio),
    String(navigator.hardwareConcurrency ?? ''),
    String(n.deviceMemory ?? ''),
    String(navigator.maxTouchPoints ?? ''),
  ].join('|');
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(traits));
  const hex = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
  return `${id}.${hex}`;
}

export function deviceHeader(): Promise<string> {
  cached ??= compute().catch(() => '');
  return cached;
}

let os: Promise<string | null> | null = null;
/**
 * The Android version from Chrome's client hints ("14"), for the offers the networks target by
 * version: Chrome's user agent says "Android 10" on every phone. Null elsewhere (iPhones' Safari
 * puts the real version in its user agent).
 */
export function androidVersion(): Promise<string | null> {
  os ??= (async () => {
    const ua = (
      navigator as Navigator & {
        userAgentData?: {
          platform?: string;
          getHighEntropyValues?: (hints: string[]) => Promise<{ platformVersion?: string }>;
        };
      }
    ).userAgentData;
    if (ua?.platform !== 'Android' || !ua.getHighEntropyValues) return null;
    const { platformVersion } = await ua.getHighEntropyValues(['platformVersion']);
    const m = /^(\d{1,2})(?:\.(\d{1,2}))?/.exec(platformVersion ?? '');
    return m ? (m[2] && m[2] !== '0' ? `${m[1]}.${m[2]}` : m[1]!) : null;
  })().catch(() => null);
  return os;
}
